import React from 'react';
import { useDispatch } from 'react-redux';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { ThinkingOrb } from 'thinking-orbs';
import AssistantResponseContent from '../../LensIntelligence/structured/AssistantResponseContent';
import askStyles from '../../LensIntelligence/askAi.module.css';
import { PostSummarizeFiles } from '../../../redux/dataroomSlice';
import styles from '../css/vdr.module.css';

/** Matches AZ_RAG_RETRIEVE_ALL_MAX_FILES default. The API enforces the live cap. */
export const SUMMARIZE_FILE_CAP = 35;

const coverageLine = coverage => {
    if (!coverage) return '';
    const fileCount = Array.isArray(coverage.files) ? coverage.files.length : 0;
    const read = Number(coverage.chunks_read) || 0;
    const expected = Number(coverage.chunks_expected) || 0;
    const fileLabel = fileCount === 1 ? 'file' : 'files';
    return `Read ${read.toLocaleString()} of ${expected.toLocaleString()} chunks across ${fileCount} ${fileLabel}.`;
};

const errorMessage = payload => {
    if (payload && typeof payload.detail === 'string' && payload.detail.trim()) return payload.detail;
    return 'Could not summarise these files.';
};

const SummarizePanel = ({ files = [], projectId, workstreamId, onClose }) => {
    const dispatch = useDispatch();
    const panelRef = React.useRef(null);
    const [instruction, setInstruction] = React.useState('');
    const [answer, setAnswer] = React.useState('');
    const [coverage, setCoverage] = React.useState(null);
    const [error, setError] = React.useState('');
    const [loading, setLoading] = React.useState(false);

    const fileKey = files.map(file => file.id).join(',');
    const overCap = files.length > SUMMARIZE_FILE_CAP;
    const unindexedIds = new Set(
        (coverage?.files || []).filter(file => Number(file.chunks_read) === 0).map(file => String(file.file_id)),
    );

    React.useLayoutEffect(() => {
        const panel = panelRef.current;
        if (!panel) return undefined;
        const sync = () => {
            const host = panel.closest('[class*="body"]');
            const top = host ? host.getBoundingClientRect().top : 0;
            panel.style.top = `${Math.max(0, Math.round(top))}px`;
        };
        sync();
        window.addEventListener('resize', sync);
        return () => window.removeEventListener('resize', sync);
    }, []);

    React.useEffect(() => {
        setAnswer('');
        setCoverage(null);
        setError('');
    }, [fileKey]);

    const submit = async () => {
        if (loading || overCap) return;
        if (!projectId || !workstreamId) {
            setError('Select a project and workstream before summarising.');
            return;
        }
        setLoading(true);
        setError('');
        setAnswer('');
        setCoverage(null);
        try {
            const data = await dispatch(
                PostSummarizeFiles({
                    projectId,
                    wsId: workstreamId,
                    body: {
                        file_ids: files.map(file => file.id),
                        instruction: instruction.trim(),
                    },
                }),
            ).unwrap();
            setAnswer(typeof data?.answer === 'string' ? data.answer : '');
            setCoverage(data?.coverage || null);
        } catch (err) {
            setError(errorMessage(err));
            if (err && err.coverage) setCoverage(err.coverage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <aside ref={panelRef} className={`${styles.panel} ${styles.fileDrawer} ${styles.summarizeDrawer}`}>
            <div className={`${styles.panelHeader} ${styles.fileDrawerHeader}`}>
                <h3 className={`${styles.panelTitle} ${styles.fileDrawerTitle}`}>Summarize</h3>
                <button type="button" className={`${styles.iconBtn} ${styles.fileDrawerClose}`} onClick={onClose} aria-label="Close">
                    <Dismiss20Regular />
                </button>
            </div>
            <div className={`${styles.panelBody} ${styles.fileDrawerBody}`}>
                <h4 className={styles.sectionHeading}>Selected files</h4>
                <ul className={styles.summarizeFileList}>
                    {files.map(file => (
                        <li key={file.id} className={styles.summarizeFileItem}>
                            <span className={styles.summarizeFileName}>{file.name}</span>
                            {unindexedIds.has(String(file.id)) ? <span className={styles.summarizeFileMeta}>Not indexed yet</span> : null}
                        </li>
                    ))}
                </ul>
                <label className={styles.fieldLabel} htmlFor="vdr-summarize-instruction">
                    Instruction
                </label>
                <textarea
                    id="vdr-summarize-instruction"
                    className={styles.textarea}
                    value={instruction}
                    placeholder="Optional. Ask for a diagram or a highlight. The summary still covers every file."
                    onChange={event => setInstruction(event.target.value)}
                    disabled={loading}
                />
                {overCap ? (
                    <p className={styles.summarizeError}>
                        Summarize allows at most {SUMMARIZE_FILE_CAP} files. Clear some files and try again.
                    </p>
                ) : null}
                <button type="button" className={styles.primaryBtn} onClick={submit} disabled={loading || overCap || files.length === 0}>
                    Summarize
                </button>
                {loading ? (
                    <div className={askStyles.thinkingLoaderWrap} role="status" aria-live="polite">
                        <div className={askStyles.thinkingLoader}>
                            <div className={askStyles.thinkingLoaderOrb}>
                                <ThinkingOrb state="composing" size={64} speed={2} theme="light" aria-label="Reading every chunk" />
                            </div>
                            <span className={askStyles.thinkingLoaderLabel}>Reading every chunk…</span>
                        </div>
                    </div>
                ) : null}
                {error ? <p className={styles.summarizeError}>{error}</p> : null}
                {answer ? (
                    <div className={styles.summarizeAnswer}>
                        <AssistantResponseContent
                            answer={answer}
                            responseBlocks={[]}
                            consultantLayout={false}
                            className={askStyles.assistantMarkdown}
                        />
                    </div>
                ) : null}
                {coverage?.complete ? <p className={styles.summarizeCoverage}>{coverageLine(coverage)}</p> : null}
            </div>
        </aside>
    );
};

export default SummarizePanel;
